import * as T from "three/webgpu";
import Experience from "./Experience";
import { OrbitControls } from "three/addons/controls/OrbitControls";

const DEFAULT_CHASE_OFFSET = new T.Vector3(0, 4, -10);
const DEFAULT_CHASE_LOOK = new T.Vector3(0, 1, 8);
const CHASE_LERP = 0.12;

export default class Camera {
  constructor() {
    this.experience = new Experience();

    this.sizes = this.experience.sizes;
    this.scene = this.experience.scene;
    this.canvas = this.experience.canvas;
    this.debug = this.experience.debug;

    this.chaseTarget = null;
    this.chaseOffset = DEFAULT_CHASE_OFFSET.clone();
    this.chaseLook = DEFAULT_CHASE_LOOK.clone();

    this._desiredPos = new T.Vector3();
    this._desiredLook = new T.Vector3();
    this._currentLook = new T.Vector3();

    this._shakeAmp = 0;
    this._shakeDecay = 0;
    this._shakeOffset = new T.Vector3();

    this.setInstance();
    this.setOrbitControls();
  }

  shake(amplitude = 0.25, decay = 6) {
    if (amplitude > this._shakeAmp) this._shakeAmp = amplitude;
    this._shakeDecay = decay;
  }

  setInstance() {
    this.instance = new T.PerspectiveCamera(
      45,
      this.sizes.width / this.sizes.height,
      0.1,
      300
    );
    this.instance.position.set(6, 4, 8);
    this.scene.add(this.instance);
  }

  setOrbitControls() {
    this.controls = new OrbitControls(this.instance, this.canvas);
    this.controls.enableDamping = true;
    if (!this.debug.active) {
      this.controls.enabled = false;
    }
  }

  setChaseTarget(target, offset, lookOffset) {
    this.chaseTarget = target;
    if (offset) this.chaseOffset.copy(offset);
    if (lookOffset) this.chaseLook.copy(lookOffset);
    if (!target) return;

    this._desiredPos.copy(target.position).add(this.chaseOffset);
    this.instance.position.copy(this._desiredPos);

    this._currentLook.copy(target.position).add(this.chaseLook);
    this.instance.lookAt(this._currentLook);
  }

  resize() {
    this.instance.aspect = this.sizes.width / this.sizes.height;
    this.instance.updateProjectionMatrix();
  }

  update() {
    if (this.chaseTarget && !this.debug.active) {
      // Undo last frame's shake offset before lerping toward the un-shaken desired pos,
      // so chase lerp doesn't compound or dampen the shake.
      this.instance.position.sub(this._shakeOffset);

      this._desiredPos.copy(this.chaseTarget.position).add(this.chaseOffset);
      this.instance.position.lerp(this._desiredPos, CHASE_LERP);

      if (this._shakeAmp > 0.001) {
        const dt = Math.min(this.experience.time.delta * 0.001, 0.05);
        this._shakeOffset.set(
          (Math.random() - 0.5) * 2 * this._shakeAmp,
          (Math.random() - 0.5) * 2 * this._shakeAmp,
          (Math.random() - 0.5) * 2 * this._shakeAmp
        );
        this._shakeAmp = Math.max(0, this._shakeAmp - this._shakeDecay * dt);
      } else {
        this._shakeOffset.set(0, 0, 0);
        this._shakeAmp = 0;
      }
      this.instance.position.add(this._shakeOffset);

      this._desiredLook.copy(this.chaseTarget.position).add(this.chaseLook);
      this._currentLook.lerp(this._desiredLook, CHASE_LERP);
      this.instance.lookAt(this._currentLook);
    } else {
      this.controls.update();
    }
  }
}
